import { enqueueSnackbar } from 'notistack';
import { createAction } from 'redux-actions';

import { resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';
import { getMessage } from '@gef-ui/localization/messages';

import { EMPTY_VALUES } from 'constants/general';
import collapsibleTable from 'features/common/collapsibleTable';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import { getUser } from 'features/views/user/selectors';
import userContext from 'features/views/userContext';
import { cleanse } from 'utils';

import {
	APPEND_SEMAPHORE_LIST,
	RESET_FEEDBACKS,
	RESET_REDUCER,
	REVERT_FEEDBACK,
	SET_FEEDBACK,
	SET_SEMAPHORE_DASHBOARD,
	SET_SEMAPHORE_LIST,
	UPDATE_SEMAPHORE_RECORD,
} from './actionTypes';
import { contactAgeIds, dialogs, formDataFieldsMap, REQUEST_SIZE_LIMIT } from './constants';
import { FIELDS, SEMAPHORE_FORM_NAME } from './form';
import { searchSemaphoreListBodyFactory } from './utils';

const { oneToTwoYears, threeToFourYears, fiveToSixYears, sevenAndLaterYears } = contactAgeIds;

export const setFeedback = createAction(SET_FEEDBACK, (recordId, feedbackStateId) => ({ recordId, feedbackStateId }));
export const revertFeedback = createAction(REVERT_FEEDBACK, (recordId, originalFeedbackId) => ({
	recordId,
	originalFeedbackId,
}));
export const resetFeedbacks = createAction(RESET_FEEDBACKS);
export const updateSemaphoreRecord = createAction(UPDATE_SEMAPHORE_RECORD, (path, updatedValue, partnerId) => ({
	path,
	updatedValue,
	partnerId,
}));

export const appendSemaphoreList = createAction(APPEND_SEMAPHORE_LIST, (values) => ({ values }));
export const setSemaphoreList = createAction(SET_SEMAPHORE_LIST, (values) => ({ values }));
export const setSemaphoreDashboard = createAction(SET_SEMAPHORE_DASHBOARD, (result) => ({ result }));
export const resetSemaphoreReducer = createAction(RESET_REDUCER);

export const resetFormCriteria =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		sendControlNext(SEMAPHORE_FORM_NAME, resetForm());
		dispatch(setSemaphoreList(EMPTY_VALUES.ARRAY));
		dispatch(fetch.actions.clearFetchStore(fetch.constants.urls.semaphoreList));
		dispatch(dialog.actions.close(dialogs.TOO_MANY_RESULTS));
		dispatch(fetchSemaphoreDashboard());
	};

export const reset = () => (dispatch) => {
	dispatch(resetSemaphoreReducer());
	dispatch(fetch.actions.clearFetchStore(fetch.constants.urls.semaphoreList));
	dispatch(fetch.actions.clearFetchStore(fetch.constants.urls.semaphoreDashboard));
};

export const changeSelectedFeedback = (record, feedbackState) => (dispatch) => {
	dispatch(setFeedback(record.partnerId, Number(feedbackState.id)));
};

export const changeSelectedFeedbackQuickType = (record, feedbackState) => (dispatch, getState) => {
	const { feedback: originalFeedback, partnerId } = record;

	const userLogin = getUser(getState());
	const contextUser = userContext.selectors.getContextUser(getState());

	let finalFeedback;
	if (originalFeedback?.feedbackId === Number(feedbackState.id)) {
		finalFeedback = EMPTY_VALUES.STRING;
	} else {
		finalFeedback = Number(feedbackState.id);
	}

	const body = {
		feedbackValueId: finalFeedback,
		partnerId,
		userName: contextUser ? contextUser.login : userLogin,
	};

	dispatch(
		fetch.actions.put(fetch.constants.urls.semaphoreSaveFeedbackValue, {
			body,
			onSuccess: [
				() => () => {
					dispatch(updateSemaphoreRecord(['feedback', 'feedbackId'], finalFeedback, record.partnerId));
					enqueueSnackbar(getMessage('toastr:semaphoreFeedbackDetailUpdated'), { variant: 'success' });
				},
			],
		}),
	);
};

export const prefillFormAndSearch =
	(type, data) =>
	(dispatch, getState, { sendControlNext }) => {
		if (type === 'impendingState') {
			sendControlNext(SEMAPHORE_FORM_NAME, resetForm());
			sendControlNext(SEMAPHORE_FORM_NAME, setValue({ fieldName: FIELDS.impendingState, value: data }));
		}

		if (type === 'halfDoughnut') {
			let finalValue;
			switch (data) {
				case 'Green':
					finalValue = oneToTwoYears;
					break;
				case 'Orange':
					finalValue = threeToFourYears;
					break;
				case 'Red':
					finalValue = fiveToSixYears;
					break;
				case 'Grey':
					finalValue = sevenAndLaterYears;
					break;
			}

			sendControlNext(SEMAPHORE_FORM_NAME, resetForm());
			sendControlNext(SEMAPHORE_FORM_NAME, setValue({ fieldName: FIELDS.semaphore, value: String(finalValue) }));
		}

		dispatch(searchSemaphoreList());
	};

export const fetchSemaphoreDashboard = () => (dispatch, getState) => {
	const userLogin = getUser(getState());
	const contextUser = userContext.selectors.getContextUser(getState());
	const userName = contextUser ? contextUser.login : userLogin;

	dispatch(
		fetch.actions.get(fetch.constants.urls.semaphoreDashboard, {
			queryParams: {
				userName,
			},
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
			onSuccess: [
				({ body }) =>
					() => {
						dispatch(setSemaphoreDashboard(body));
					},
			],
		}),
	);
};

export const searchSemaphoreList =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(SEMAPHORE_FORM_NAME);
		const userLogin = getUser(getState());
		const contextUser = userContext.selectors.getContextUser(getState());
		const userName = contextUser ? contextUser.login : userLogin;

		const finalBody = searchSemaphoreListBodyFactory(formData, userName);

		dispatch(
			fetch.actions.post(fetch.constants.urls.semaphoreList, {
				queryParams: { size: 10, page: 0 },
				body: finalBody,
				onSuccess: [
					({ body }) =>
						() => {
							if (body?.pageMetaData?.totalRows > REQUEST_SIZE_LIMIT) {
								dispatch(dialog.actions.open(dialogs.TOO_MANY_RESULTS, null, dialog.constants.ERROR));
							} else {
								dispatch(collapsibleTable.actions.removeOpenedRows('SemaphoreResultsTable'));
								dispatch(dialog.actions.close(dialogs.TOO_MANY_RESULTS));
							}
							const cleanFormData = cleanse(formData);
							pushToFilterDataLayer({
								filterSection: 'Semafor',
								formData: cleanFormData,
								formDataFieldsMap,
							});

							dispatch(setSemaphoreList(body.data));
						},
				],
			}),
		);
	};

export const loadMore = () => (dispatch, getState) => {
	const { queryParams, body } = fetch.selectors.getLastRequest(fetch.constants.urls.semaphoreList)(getState());
	const updatedQueryParams = {
		...queryParams,
		page: queryParams.page + 1,
	};

	dispatch(
		fetch.actions.post(fetch.constants.urls.semaphoreList, {
			queryParams: updatedQueryParams,
			body,
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					const { data } = response.body;
					dispatch(appendSemaphoreList(data));
				},
			],
		}),
	);
};

export const loadPage = (page) => (dispatch, getState) => {
	const { queryParams, body } = fetch.selectors.getLastRequest(fetch.constants.urls.semaphoreList)(getState());
	const updatedQueryParams = {
		...queryParams,
		page,
	};

	dispatch(
		fetch.actions.post(fetch.constants.urls.semaphoreList, {
			queryParams: updatedQueryParams,
			body,
			onSuccess: [
				(response) => () => {
					if (!response.body) return;
					const { data } = response.body;
					dispatch(setSemaphoreList(data));
				},
			],
		}),
	);
};
